import { AdjustmentsService } from '@amzn/awscat-portfolio-assessment-service-client';
import { ManualAdjustment } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, SpaceBetween, Toggle } from '@amzn/awsui-components-react';
import React, { useState } from 'react';
import { match, useRouteMatch } from 'react-router';

import { constants } from '../../../common/Localization';
import { MatchParam } from '../../../common/types';
import ConfirmationModal from '../../common/ConfirmationModal';
import {  
    getAdjustmentMap, 
    getManualAdjustmentWithAwsOrOnPremRemoved, 
    getTitleToDisplay, 
    getUnSelectedCorresponding, 
    showDeleteCorrespondingOption
} from './adjustments-helper';
import { AdjustmentItem, AdjustmentItemSelected } from './interfaces';

const adjustmentService = new AdjustmentsService();

type Props = {
    selectedAdjustments: AdjustmentItem[];
    allAdjustments: AdjustmentItem[];
    dismissModal: () => void;
    refreshData: () => void;
} & FlashContextInterface;

function DeleteAdjustmentsModal({ selectedAdjustments, allAdjustments, dismissModal, refreshData, flash }: Props): JSX.Element {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });

    const portfolioId = match?.params.portfolioId ?? null;

    const [ deleteLoading, setDeleteLoading ] = useState(false);
    const [ alsoDeleteCorrespondingAdjustments, setAlsoDeleteCorrespondingAdjustments ] = useState(false);

    const adjustmentsMap: Map<string, AdjustmentItemSelected[]> = 
            alsoDeleteCorrespondingAdjustments ? getAdjustmentMap(selectedAdjustments, allAdjustments) : getAdjustmentMap(selectedAdjustments);

    const onConfirmDelete = async () => {
        if (!portfolioId) {
            return;
        }
        setDeleteLoading(true);
        try {
            if (alsoDeleteCorrespondingAdjustments) {
                await adjustmentService.deleteAdjustments(portfolioId, Array.from(adjustmentsMap.keys()));
            } else {
                const deleteAdjustmentIds: string[] = [];
                const updateAdjustments: ManualAdjustment[] = [];
                adjustmentsMap.forEach((adjustments: AdjustmentItemSelected[], id: string) => {
                    // if adjustments length is 2 then both the aws and on-prem properties of an adjustment have been selected
                    if (adjustments.length === 2) {
                        deleteAdjustmentIds.push(id);
                    } else {
                        const unSelectedCorresponding = getUnSelectedCorresponding(adjustments[0], allAdjustments);
                        if (unSelectedCorresponding) {
                            updateAdjustments.push(getManualAdjustmentWithAwsOrOnPremRemoved(unSelectedCorresponding));
                        } else {
                            deleteAdjustmentIds.push(id);
                        }
                    }
                });
                if (deleteAdjustmentIds.length > 0) {
                    await adjustmentService.deleteAdjustments(portfolioId, deleteAdjustmentIds);
                }
                if (updateAdjustments.length > 0) {
                    await adjustmentService.updateAdjustments(portfolioId, updateAdjustments);
                }
            }
            refreshData();
            dismissModal();
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_DELETING_ADJUSTMENTS);
        } finally {
            setDeleteLoading(false);
        }
    };

    const getListOfAdjustmentTitles = () => {
        const titlesToDisplay = [];
        for (const adjustmentArray of adjustmentsMap.values()) {
            titlesToDisplay.push( 
                <li key={adjustmentArray[0].adjustmentId}>
                    <SpaceBetween size='xs' direction='horizontal'>
                        {adjustmentArray.map((adjustment, index) => (
                            <SpaceBetween size='xs' direction='horizontal' key={`${adjustment.adjustmentId}${index}`}>
                                <Box fontWeight={adjustment.selected ? 'bold' : 'normal'} display='inline'>{getTitleToDisplay(adjustment)}</Box>
                                <Box 
                                    fontWeight='light' 
                                    display='inline' 
                                    variant='small'
                                >
                                    {`(${adjustment.type})${index === 0 && adjustmentArray.length === 2 ? ', ': ''}`}
                                </Box>
                            </SpaceBetween>
                        ))}
                    </SpaceBetween>
                </li>
            );
        }

        return (
            <ul data-testid='deleteList'>
                {titlesToDisplay}
            </ul>
        );
    };

    const deleteCorrespondingToggle = showDeleteCorrespondingOption(selectedAdjustments, allAdjustments) && (
        <Toggle 
            checked={alsoDeleteCorrespondingAdjustments}
            onChange={({ detail }) => setAlsoDeleteCorrespondingAdjustments(detail.checked)}
        >
            {constants.ALSO_DELETE_CORRESPONDING} {alsoDeleteCorrespondingAdjustments ? ` (${constants.SELECTED_ADJUSTMENTS_IN_BOLD})`: ''}
        </Toggle>
    );

    return (
        <ConfirmationModal
            friction='delete'
            visible
            header={constants.DELETE_SELECTED_ADJUSTMENTS}
            warning={constants.THIS_ACTION_CANNOT_BE_UNDONE}
            onConfirm={onConfirmDelete}
            confirmButton={constants.DELETE_BUTTON_TEXT}
            confirmLoading={deleteLoading}
            onDismiss={dismissModal}
            onCancel={dismissModal}>
            <SpaceBetween size='xs'>
                <Box padding={{top: 'm'}}>{constants.YOU_ARE_ABOUT_TO_DELETE}</Box>
                <Box>{getListOfAdjustmentTitles()}</Box>
                <Box padding={{bottom: 'm'}}>{deleteCorrespondingToggle}</Box>
            </SpaceBetween>
        </ConfirmationModal>
    );
}

export default withFlashContext(DeleteAdjustmentsModal);
